/**
 * Tracked vs Actual — the two books held side by side, and the gap between
 * them. Never their sum.
 *
 * "Tracked" is what a job was costed at, out of its Daily Reports, at the
 * owner's own rates (pnl.ts / pours). "Banked" is what actually moved
 * through the bank account for that job (breakdown.ts, from imported
 * statements). The entire feature this module belongs to exists because
 * those two were once added together and the same cement got counted
 * twice — a poured-and-costed load, and the card payment that later paid
 * for it, both landing in the same total. A reader who finds themselves
 * writing `tracked.revenue + banked.money_in` has re-created that exact
 * bug. This module only ever subtracts one side from the other, or leaves
 * the gap null when a side is missing — it never combines them into one
 * number.
 *
 * `TrackedFigures` and `BankedFigures` (src/lib/types.ts) use different
 * property names on purpose — revenue/cost/margin vs money_in/money_out/net
 * — so an accidental sum reads wrong at a glance and the reader stays aware
 * of which book a value came from.
 *
 * Everything here is PURE and, per the rule in ./numbers.ts, never throws:
 * every computed value is a finite number, or null where there is nothing to
 * compute it from — including a gap that needs a side the job doesn't have.
 * A job with no bank lines has not collected nothing, it has nothing to
 * compare; 0 would read as "fully paid", which is the opposite of the truth.
 */

import { roundTo } from './numbers';
import type { BankedFigures, JobComparison, TrackedFigures } from '../types';

function money(value: number): number {
  return roundTo(value, 2) ?? 0;
}

/* ==========================================================================
 * One job, both books
 * ======================================================================== */

/**
 * One job's tracked and banked figures, side by side, with the two gaps
 * between them. Either gap is null whenever the side it needs is missing —
 * never 0, which would read as "settled".
 */
export function compareJob(
  tracked: TrackedFigures | null,
  banked: BankedFigures | null,
  siteId: number | null,
  name: string
): JobComparison {
  return {
    site_id: siteId,
    name,
    tracked,
    banked,
    still_to_collect: tracked && banked ? money(tracked.revenue - banked.money_in) : null,
    beyond_costed: tracked && banked ? money(banked.money_out - tracked.cost) : null
  };
}

/* ==========================================================================
 * Ranking
 * ======================================================================== */

/**
 * `rows`, best margin first, worst last — a stable sort with null margins
 * pushed to the very end. A row with no margin has no revenue to be a
 * percentage of, so it is not the worst performer, it is unrankable; putting
 * it last (rather than treating it as -Infinity or 0) keeps that distinct.
 * Stability keeps rows with equal margin in their original relative order,
 * so the list does not visibly reshuffle between renders of the same data.
 */
export function rankBy<T>(rows: readonly T[], marginPct: (row: T) => number | null): T[] {
  return rows
    .map((row, index) => ({ row, index, value: marginPct(row) }))
    .sort((a, b) => {
      if (a.value === null || b.value === null) {
        if (a.value === null && b.value === null) return a.index - b.index;
        return a.value === null ? 1 : -1;
      }
      if (a.value !== b.value) return b.value - a.value;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}

/**
 * For every site id present in BOTH orderings, how many places it moved
 * between them — `abs(indexInTracked - indexInBanked)`. A job the tracked
 * ranking calls #1 and the banked ranking calls #3 has a gap of 2; a job
 * that lands in the same spot in both has a gap of 0.
 *
 * An id present in only one ordering has nothing to compare and is left out
 * of the map entirely, rather than guessing at a gap for it — this is the
 * ordinary case for a job that has been tracked but not yet banked. See the
 * partial-overlap case in compare.test.ts ("leaves out a job tracked but not
 * yet banked") for proof this stays omitted rather than becoming a 0.
 */
export function rankDisagreement(
  trackedOrder: readonly (number | null)[],
  bankedOrder: readonly (number | null)[]
): Map<number | null, number> {
  const bankedIndexOf = new Map<number | null, number>();
  bankedOrder.forEach((id, index) => {
    if (!bankedIndexOf.has(id)) bankedIndexOf.set(id, index);
  });

  const gaps = new Map<number | null, number>();
  trackedOrder.forEach((id, trackedIndex) => {
    const bankedIndex = bankedIndexOf.get(id);
    if (bankedIndex === undefined) return;
    gaps.set(id, Math.abs(trackedIndex - bankedIndex));
  });
  return gaps;
}

/**
 * Net as a share of what came in, on the BANKED side alone — the banked book
 * carries no margin of its own, and it must not borrow the tracked one. Null,
 * not 0%, when nothing has come in for the job: a job with costs and no
 * deposits has no margin to be a percentage of.
 */
export function clearedMarginPct(row: JobComparison): number | null {
  if (row.banked === null || row.banked.money_in === 0) return null;
  return (row.banked.net / row.banked.money_in) * 100;
}

/**
 * The "moved N places" ranking for the job-comparison section — tracked and
 * banked orderings, and the gap between them, restricted to jobs present in
 * BOTH books.
 *
 * `rankDisagreement` requires its two orderings to share membership (its own
 * doc comment: an id present in only one is left out, not given a guessed
 * gap). Ranking each book's FULL list of jobs and feeding those straight in
 * would compare a job's place in a ten-job list against its place in a
 * two-job list and call the difference "moved 7 places" — see the
 * mismatched-length case in compare.test.ts, and the realistic one against
 * this function right below it. Rank both sides over the intersection
 * instead, so a place moved is a place moved within the same list.
 */
export function rankBothBooks(rows: readonly JobComparison[]): {
  tracked: JobComparison[];
  banked: JobComparison[];
  moved: Map<number | null, number>;
} {
  const bothBooked = rows.filter((row) => row.tracked !== null && row.banked !== null);
  const tracked = rankBy(bothBooked, (row) => row.tracked?.margin_pct ?? null);
  const banked = rankBy(bothBooked, clearedMarginPct);
  const moved = rankDisagreement(
    tracked.map((row) => row.site_id),
    banked.map((row) => row.site_id)
  );
  return { tracked, banked, moved };
}
